import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { groupsClient, tagsClient, usersClient } from '../../apis/client-instances';
import { Group, Login, Tag, TagType, UserGroup, UserRole, Workspace } from '../../apis/api-client';
import AssignContentDialog from '../users/AssignContentDialog';
import ConfirmDialog from '../ConfirmDialog';
import PagedTable, { SortDirection } from '../common/PagedTable';
import { usePagedList } from '../../hooks/usePagedList';
import { Box } from '../ui';
import CreateGroupDialog from './groupsDirectory/CreateGroupDialog';
import GroupBulkActionBar from './groupsDirectory/GroupBulkActionBar';
import GroupRowMenu from './groupsDirectory/GroupRowMenu';
import GroupsDirectoryHeader from './groupsDirectory/GroupsDirectoryHeader';
import { buildGroupColumns } from './groupsDirectory/groupColumns';
import { alert, uploadFile } from '../../hooks/function';
import { RootState } from '../../types';
import { isAccountOrSystemAdmin } from '../../utils/roleHelpers';

interface GroupsDirectoryProps {
  /**
   * When set, the directory is LOCKED to one workspace (the Workspaces
   * editor's Groups tab): the account-wide workspace filter is hidden,
   * the Workspace column is dropped, and the per-group admin-count +
   * content-tag columns — which need a single workspace to resolve
   * against — are shown. When omitted, the directory lists groups
   * account-wide (the standalone /groups page) with a workspace filter
   * for account admins and a leaner column set.
   */
  workspaceIdLock?: number;
  /** Workspace name for the create-dialog subtitle when locked. */
  workspaceName?: string;
}

/**
 * GroupsDirectory
 *
 * The single group roster surface, mirroring UsersDirectory: a paged +
 * searchable table with create / bulk-assign / bulk-delete and a per-row
 * kebab (Edit → full Group editor, Assign content, Delete). Rendered by
 * both the standalone /groups page (unlocked, account-wide) and the
 * Workspaces editor's Groups tab (`workspaceIdLock` set, single
 * workspace + richer columns). Editing always navigates to the full
 * Group editor page; the workspace context comes from the lock or, when
 * unlocked, the group's own `workspaceId`.
 */
export default function GroupsDirectory({ workspaceIdLock, workspaceName }: GroupsDirectoryProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useSelector((state: RootState) => state.profile.user);
  const userRole = currentUser?.role;

  const locked = workspaceIdLock != null;

  // Global switcher pins the workspace scope when set; the page-level
  // filter only applies when the switcher is "All Workspaces". Neither
  // matters when locked — the lock overrides both.
  const switcherWorkspace = useSelector((state: RootState) => state.workspaces.selectedWorkspace);
  const [pageWorkspaceFilter, setPageWorkspaceFilter] = useState<Workspace | null>(null);
  const filterWorkspace = switcherWorkspace ?? pageWorkspaceFilter;
  const effectiveWorkspaceId = workspaceIdLock ?? filterWorkspace?.id;
  // Account admins get a page-level workspace filter on the account-wide
  // view; a pinned global switcher already constrains scope, so the
  // redundant page filter hides then.
  const showWorkspaceFilter = !locked && isAccountOrSystemAdmin(userRole) && !switcherWorkspace;

  // Per-row modify gate. In the locked workspace view every visible row
  // is in-scope; on the account-wide view the server already scopes the
  // result set, but we keep the defensive check the standalone page used.
  const canModifyGroup = (g: Group): boolean => {
    if (locked) return true;
    if (userRole === UserRole.Admin) return true;
    if (userRole === UserRole.AccountAdmin) return g.accountId === currentUser?.accountId;
    if (userRole === UserRole.WorkspaceAdmin) return g.workspaceId === currentUser?.workspaceId;
    return false;
  };

  // Create-group dialog state. Holds the initial settings the dialog
  // collects — name + description + branding. Access rules and members
  // are configured on the group's edit page after creation since they
  // require the group id to exist first.
  const [groupCreating, setGroupCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('');
  const [newGroupLogo, setNewGroupLogo] = useState<File | null>(null);
  const [newGroupLogoPreview, setNewGroupLogoPreview] = useState<string | null>(null);

  // New groups land in the locked workspace, or — on the account-wide
  // view — the single workspace currently in scope (switcher or filter).
  // With "All Workspaces" there's no target, so creation is disabled.
  const createTargetWorkspaceId = workspaceIdLock ?? filterWorkspace?.id;
  const createTargetName = locked ? workspaceName : filterWorkspace?.name;

  const resetCreateDialog = () => {
    setNewGroupName('');
    setNewGroupDescription('');
    setNewGroupColor('');
    setNewGroupLogo(null);
    setNewGroupLogoPreview(null);
  };
  const openCreateDialog = () => {
    resetCreateDialog();
    setCreateDialogOpen(true);
  };
  const closeCreateDialog = () => {
    if (groupCreating) return; // don't close mid-save
    setCreateDialogOpen(false);
    resetCreateDialog();
  };
  const handleNewGroupLogoChange = (file: File) => {
    setNewGroupLogo(file);
    setNewGroupLogoPreview(URL.createObjectURL(file));
  };
  const handleNewGroupLogoRemove = () => {
    setNewGroupLogo(null);
    setNewGroupLogoPreview(null);
  };

  // Paged groups query. Driven by usePagedList so search + pagination
  // come for free. Default pageSize=50 so most tenants see their
  // whole roster on one page; larger workspaces page naturally.
  // Column sort — resolved server-side; changing either value refetches
  // from page 1. Keys ('name' | 'users' | 'members' | 'admins') pass
  // straight through to searchGroups, which orders the full result set.
  const [groupSortBy, setGroupSortBy] = useState<string>('name');
  const [groupSortDir, setGroupSortDir] = useState<SortDirection>('asc');

  const groupsPaged = usePagedList<Group>({
    fetcher: useCallback(
      async ({ page, pageSize, search }) => {
        const result = await groupsClient.searchGroups(
          effectiveWorkspaceId ?? undefined,
          search || undefined,
          page,
          pageSize,
          undefined,
          groupSortBy,
          groupSortDir,
        );
        return {
          items: result.items ?? [],
          totalCount: result.totalCount ?? 0,
          page: result.page ?? page,
          pageSize: result.pageSize ?? pageSize,
        };
      },
      [effectiveWorkspaceId, groupSortBy, groupSortDir],
    ),
    deps: [effectiveWorkspaceId ?? null, groupSortBy, groupSortDir],
    initialPageSize: 50,
    onError: () => alert(enqueueSnackbar, 'Failed to load groups', 'error'),
  });

  // Per-group admin count + content-tag map. Both are workspace-scoped
  // enrichments that only feed the LOCKED view's richer columns, so we
  // skip the work entirely on the account-wide view.
  //
  // Member + admin counts ride on the group search row itself
  // (server-computed — see GroupRepo.SearchGroupsAsync), so there's no
  // per-row fetching here. We only resolve the workspace's content tags
  // once so the Content-tags column can render chips. Locked view only.
  const [tagById, setTagById] = useState<Record<number, Tag>>({});

  useEffect(() => {
    if (!locked || !workspaceIdLock) return;
    let cancelled = false;
    (async () => {
      try {
        const tagsRes = await tagsClient
          .searchTags(undefined, TagType.CourseTag, workspaceIdLock, true, false, undefined, 1, 500)
          .catch(() => ({ items: [] as Tag[] } as any));
        if (cancelled) return;
        const tagMap: Record<number, Tag> = {};
        for (const t of (tagsRes.items ?? []) as Tag[]) {
          if (t.id) tagMap[t.id] = t;
        }
        setTagById(tagMap);
      } catch (err) {
        console.error('Failed to load workspace content tags', err);
        if (!cancelled) setTagById({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locked, workspaceIdLock]);

  // Assign-content dialog target. Holds the group + the resolved Login
  // records for its members. We fetch members lazily on click so a tab
  // with 100 groups doesn't preload 100 group rosters upfront.
  const [assignGroup, setAssignGroup] = useState<Group | null>(null);
  const [assignTargets, setAssignTargets] = useState<Login[]>([]);
  const [assignLoading, setAssignLoading] = useState(false);
  // Bulk selection. The PagedTable's selection model gives us the ids;
  // the action handlers below translate that into per-id API calls.
  // `bulkAssignOpen` is separate from `assignGroup` so the same
  // AssignContentDialog can host a single-group or multi-group assign.
  const [selectedGroupIds, setSelectedGroupIds] = useState<(string | number)[]>([]);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [rowMenu, setRowMenu] = useState<{ anchorEl: HTMLElement; row: Group } | null>(null);
  const closeRowMenu = () => setRowMenu(null);
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null);
  const [deleteRunning, setDeleteRunning] = useState(false);

  // Assigning content needs a workspace to hydrate member Logins against.
  // A single group carries its own workspaceId; a bulk assign needs all
  // picked groups to share one workspace, so it's only offered when a
  // single workspace is in scope (locked, or filtered on the page).
  const bulkAssignWorkspaceId = workspaceIdLock ?? filterWorkspace?.id;

  const hydrateMemberLogins = async (workspaceId: number, userIds: number[]): Promise<Login[]> => {
    if (userIds.length === 0) return [];
    const usersRes = await usersClient.searchUsersPaged(
      workspaceId,
      undefined,
      undefined,
      undefined,
      1,
      Math.max(userIds.length, 25),
      userIds,
    );
    return usersRes.items ?? [];
  };

  const openAssignForGroup = async (g: Group) => {
    const wsId = workspaceIdLock ?? g.workspaceId;
    if (!g.id || !wsId) return;
    setAssignGroup(g);
    setAssignLoading(true);
    setAssignTargets([]);
    try {
      const memberRows = await groupsClient.getGroupMembers(g.id);
      const userIds = memberRows
        .map((m: UserGroup) => m.userId)
        .filter((v): v is number => typeof v === 'number');
      setAssignTargets(await hydrateMemberLogins(wsId, userIds));
    } catch {
      alert(enqueueSnackbar, 'Failed to load group members for assignment', 'error');
      setAssignGroup(null);
    } finally {
      setAssignLoading(false);
    }
  };

  const closeAssignDialog = () => {
    setAssignGroup(null);
    setAssignTargets([]);
    setAssignLoading(false);
    setBulkAssignOpen(false);
  };

  /**
   * Bulk assign content to every member across the selected groups.
   * Aggregates user ids into a Set so a learner in two picked groups is
   * targeted once, then hydrates the Logins in one call. Requires a
   * single workspace in scope (see `bulkAssignWorkspaceId`).
   */
  const openBulkAssign = async () => {
    if (selectedGroupIds.length === 0 || !bulkAssignWorkspaceId) return;
    setBulkAssignOpen(true);
    setAssignLoading(true);
    setAssignTargets([]);
    try {
      const allUserIds = new Set<number>();
      for (const gid of selectedGroupIds) {
        const members = await groupsClient.getGroupMembers(Number(gid));
        members.forEach((m: UserGroup) => {
          if (typeof m.userId === 'number') allUserIds.add(m.userId);
        });
      }
      setAssignTargets(await hydrateMemberLogins(bulkAssignWorkspaceId, Array.from(allUserIds)));
    } catch {
      alert(enqueueSnackbar, 'Failed to load members from selected groups', 'error');
      setBulkAssignOpen(false);
    } finally {
      setAssignLoading(false);
    }
  };

  const runSingleDelete = async () => {
    if (!deleteTarget?.id || deleteRunning) return;
    const target = deleteTarget;
    setDeleteTarget(null); // close confirm before the async call
    setDeleteRunning(true);
    try {
      await groupsClient.deleteGroup(target.id);
      alert(enqueueSnackbar, `Deleted "${target.name || 'group'}"`, 'success');
      groupsPaged.refresh();
    } catch (err: any) {
      alert(
        enqueueSnackbar,
        err?.response?.data || `Failed to delete "${target.name || 'group'}"`,
        'error',
      );
    } finally {
      setDeleteRunning(false);
    }
  };

  /**
   * Bulk-delete the selected groups. Sequential fan-out with per-id
   * try/catch so one denied delete doesn't stop the rest.
   */
  const runBulkDelete = async () => {
    if (selectedGroupIds.length === 0 || bulkRunning) return;
    setBulkDeleteOpen(false);
    setBulkRunning(true);
    let ok = 0;
    let fail = 0;
    for (const gid of selectedGroupIds) {
      try {
        await groupsClient.deleteGroup(Number(gid));
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setBulkRunning(false);
    setSelectedGroupIds([]);
    groupsPaged.refresh();
    if (fail === 0) {
      alert(enqueueSnackbar, `Deleted ${ok} ${ok === 1 ? 'group' : 'groups'}`, 'success');
    } else {
      alert(enqueueSnackbar, `Deleted ${ok}; ${fail} failed`, ok > 0 ? 'warning' : 'error');
    }
  };

  const goToGroup = (g: Group) => {
    const wsId = workspaceIdLock ?? g.workspaceId;
    if (!wsId || !g.id) return;
    navigate(`/workspaces/${wsId}/groups/${g.id}`);
  };

  const handleCreateGroup = async () => {
    if (!createTargetWorkspaceId) return;
    const trimmedName = newGroupName.trim();
    if (!trimmedName) {
      alert(enqueueSnackbar, 'Give the group a name first.', 'warning');
      return;
    }
    if (newGroupColor && !/^#[0-9A-Fa-f]{6}$/.test(newGroupColor)) {
      alert(enqueueSnackbar, 'Primary color must be a valid hex (e.g., #3ea6d6)', 'error');
      return;
    }
    setGroupCreating(true);
    try {
      const created = await groupsClient.createGroup({
        name: trimmedName,
        workspaceId: createTargetWorkspaceId,
        description: newGroupDescription.trim() || undefined,
      });

      // Persist branding via updateGroupAccess. The uploaded logo (if
      // any) goes through the same SAS flow as workspace logos. The new
      // group starts with default access (no grants), so sending just
      // branding here can't clobber anything.
      let finalLogoUrl: string | null = null;
      if (newGroupLogo) {
        try {
          const upload = await uploadFile(newGroupLogo);
          finalLogoUrl = upload?.uploadedUrl || null;
        } catch {
          alert(enqueueSnackbar, 'Group created, but the logo failed to upload', 'warning');
        }
      }
      if (created.id && (newGroupColor || finalLogoUrl)) {
        try {
          await groupsClient.updateGroupAccess(created.id, {
            primaryColor: newGroupColor || undefined,
            logoUrl: finalLogoUrl || undefined,
          });
        } catch {
          alert(enqueueSnackbar, 'Group created, but the branding failed to save', 'warning');
        }
      }

      groupsPaged.refresh();
      setCreateDialogOpen(false);
      resetCreateDialog();
      alert(enqueueSnackbar, 'Group created', 'success');
      navigate(`/workspaces/${createTargetWorkspaceId}/groups/${created.id}`);
    } catch (err: any) {
      alert(enqueueSnackbar, err?.response?.data || 'Failed to create group', 'error');
    } finally {
      setGroupCreating(false);
    }
  };

  const columns = buildGroupColumns({
    locked,
    tagById,
    onOpenRowMenu: (anchorEl, row) => setRowMenu({ anchorEl, row }),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <GroupsDirectoryHeader
        totalCount={groupsPaged.totalCount}
        showWorkspaceFilter={showWorkspaceFilter}
        workspaceFilter={pageWorkspaceFilter}
        onWorkspaceFilterChange={setPageWorkspaceFilter}
        search={groupsPaged.search ?? ''}
        onSearchChange={groupsPaged.setSearch}
        createTargetWorkspaceId={createTargetWorkspaceId}
        onCreate={openCreateDialog}
      />

      <PagedTable<Group>
        columns={columns}
        rows={groupsPaged.items}
        totalCount={groupsPaged.totalCount}
        page={groupsPaged.page}
        pageSize={groupsPaged.pageSize}
        loading={groupsPaged.loading}
        rowKey={(g) => g.id ?? g.name ?? ''}
        selectable
        selectedIds={selectedGroupIds}
        onSelectionChange={setSelectedGroupIds}
        sortBy={groupSortBy}
        sortDir={groupSortDir}
        onSortChange={(key, dir) => {
          setGroupSortBy(key);
          setGroupSortDir(dir);
        }}
        onPageChange={groupsPaged.setPage}
        onPageSizeChange={groupsPaged.setPageSize}
        onRowClick={(g) => goToGroup(g)}
        emptyMessage={
          groupsPaged.search
            ? 'No groups match that search.'
            : 'No groups yet — click New Group above to start segmenting this workspace.'
        }
      />

      {/* Per-row kebab menu — Edit / Assign / Delete. */}
      <GroupRowMenu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={!!rowMenu}
        canModify={rowMenu ? canModifyGroup(rowMenu.row) : false}
        onClose={closeRowMenu}
        onEdit={() => {
          if (rowMenu) goToGroup(rowMenu.row);
        }}
        onAssign={() => {
          if (rowMenu) openAssignForGroup(rowMenu.row);
        }}
        onDelete={() => {
          if (rowMenu) setDeleteTarget(rowMenu.row);
        }}
      />

      {/* Single-row delete — typed-confirm safeguard. */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete group"
        content={`Delete "${deleteTarget?.name || 'this group'}"? This cannot be undone.`}
        confirmText="DELETE"
        onConfirm={runSingleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Assign-content dialog — hosts both single-group and multi-group
          flows; members are resolved before it opens. */}
      <AssignContentDialog
        open={(!!assignGroup || bulkAssignOpen) && !assignLoading}
        targetUsers={assignTargets}
        onClose={closeAssignDialog}
        onAssigned={() => {
          closeAssignDialog();
          setSelectedGroupIds([]);
        }}
      />

      {/* Bulk-delete typed-confirm. */}
      <ConfirmDialog
        open={bulkDeleteOpen}
        title="Delete groups"
        content={`Delete ${selectedGroupIds.length} ${selectedGroupIds.length === 1 ? 'group' : 'groups'}? This cannot be undone.`}
        confirmText="DELETE"
        onConfirm={runBulkDelete}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      {/* Bulk action bar — fixed to the viewport bottom, only when
          something's selected. Assign is offered only when a single
          workspace is in scope (it needs one to hydrate members). */}
      {selectedGroupIds.length > 0 && (
        <GroupBulkActionBar
          count={selectedGroupIds.length}
          showAssign={!!bulkAssignWorkspaceId}
          running={bulkRunning}
          onAssign={openBulkAssign}
          onDelete={() => setBulkDeleteOpen(true)}
          onClear={() => setSelectedGroupIds([])}
        />
      )}

      {/* Create-group dialog — captures the initial settings. Access
          rules + members come later on the group's edit page. */}
      <CreateGroupDialog
        open={createDialogOpen}
        name={newGroupName}
        description={newGroupDescription}
        color={newGroupColor}
        logoPreview={newGroupLogoPreview}
        creating={groupCreating}
        targetName={createTargetName}
        onNameChange={setNewGroupName}
        onDescriptionChange={setNewGroupDescription}
        onColorChange={setNewGroupColor}
        onLogoChange={handleNewGroupLogoChange}
        onLogoRemove={handleNewGroupLogoRemove}
        onClose={closeCreateDialog}
        onCreate={handleCreateGroup}
      />
    </Box>
  );
}
